"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _lodash = _interopRequireDefault(require("lodash.omit"));
var _componentsBase = require("@telus-uds/components-base");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _FullBleedContent = _interopRequireWildcard(require("../shared/FullBleedContent"));
var _AuthorDate = _interopRequireDefault(require("./AuthorDate"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// Passes React Native-oriented system props through UDS PressableCardBase
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_componentsBase.a11yProps, _componentsBase.focusHandlerProps, _componentsBase.viewProps]);

// Stop changes to the card's inner border width causing the size and
// apparent position of the full bleed image to change.
const FullBleedOffsetOuter = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "PreviewCard__FullBleedOffsetOuter",
  componentId: "components-web__sc-1yfz4de-0"
})(_ref => {
  let {
    borderOffset
  } = _ref;
  return {
    overflow: 'hidden',
    marginTop: borderOffset * 2
  };
});
const FullBleedOffsetInner = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "PreviewCard__FullBleedOffsetInner",
  componentId: "components-web__sc-1yfz4de-1"
})(_ref2 => {
  let {
    borderOffset
  } = _ref2;
  return {
    position: 'relative',
    margin: 0 - borderOffset
  };
});
const defaultTokens = {
  minWidth: 0,
  shadow: null,
  contentAlignItems: 'stretch',
  contentJustifyContent: 'flex-start',
  contentFlexGrow: 0,
  contentFlexShrink: 0
};

/**
 * Showcase and guide users to another page, with a preview containing title and a footer.
 * Whole card is interactive, width expands based on column or container.
 *
 * - Use `tag` to show category of content
 * - Use `title` to show title of the story
 * - Use `footer` to show text such as the author / date
 * - Use `href` to set the target URL
 * - Use `fullBleedContent` to set the thumbnail image
 */
const PreviewCard = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    tag,
    title,
    footer,
    fullBleedImage,
    fullBleedContent = fullBleedImage,
    onPress,
    href,
    ...rest
  } = _ref3;
  const {
    fullBleedContentPosition,
    contentStackDirection,
    fullBleedContentProps
  } = (0, _FullBleedContent.useFullBleedContentProps)({
    ...fullBleedContent,
    position: 'bottom'
  });
  const {
    components: {
      PreviewCard: theme
    }
  } = (0, _componentsBase.useTheme)();
  const getPressableCardTokens = cardState => ({
    ...(0, _lodash.default)((0, _componentsBase.getThemeTokens)(theme, {}, {}, cardState), 'separatorColor'),
    ...defaultTokens
  });
  const {
    hrefAttrs,
    rest: unusedRest
  } = _componentsBase.hrefAttrsProp.bundle(rest);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.PressableCardBase, {
    onPress: onPress,
    href: href,
    hrefAttrs: hrefAttrs,
    tokens: getPressableCardTokens,
    ref: ref,
    ...selectProps(unusedRest),
    children: cardState => {
      const {
        borderRadius,
        borderWidth
      } = getPressableCardTokens(cardState);
      // Stop content jumping around as border size changes
      const borderOffset = borderWidth - theme.tokens.borderWidth;
      const fullBleedBorderRadius = (0, _FullBleedContent.getFullBleedBorderRadius)(borderRadius + borderOffset, fullBleedContentPosition);
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_componentsBase.StackView, {
        direction: contentStackDirection,
        tokens: {
          justifyContent: 'space-between',
          flexGrow: 1
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_componentsBase.Box, {
          horizontal: {
            xs: 4,
            lg: 5,
            options: {
              subtract: borderOffset
            }
          },
          vertical: {
            xs: 5,
            lg: 7,
            options: {
              subtract: borderOffset
            }
          },
          children: [Boolean(tag) && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
              variant: {
                size: 'eyebrow'
              },
              children: tag
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Spacer, {
              space: 2
            })]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
            variant: {
              size: 'h4'
            },
            children: title
          }), Boolean(footer) && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Spacer, {
              space: 2
            }), typeof footer === 'string' ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
              variant: {
                size: 'small'
              },
              children: footer
            }) : footer]
          })]
        }), Boolean(fullBleedContentProps.src || fullBleedContentProps.content) && /*#__PURE__*/(0, _jsxRuntime.jsx)(FullBleedOffsetOuter, {
          borderOffset: borderOffset,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(FullBleedOffsetInner, {
            borderOffset: borderOffset,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_FullBleedContent.default, {
              borderRadius: fullBleedBorderRadius,
              ...fullBleedContentProps
            })
          })
        })]
      });
    }
  });
});
PreviewCard.displayName = 'PreviewCard';

// Provide standard author/date footer as a preset with the export
PreviewCard.AuthorDate = _AuthorDate.default;
PreviewCard.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _componentsBase.getTokensPropType)('PreviewCard'),
  /**
   * The URL of the story to be navigated to. Note that `Video` component does not play well when used within
   * a `PreviewCard` with `href` prop set (the volume and progress sliders can be clicked but are not draggable).
   * Please use `onPress` prop instead `href` to organize navigation in cases like this.
   *
   * @see https://github.com/telus/allium-design-system/issues/6
   */
  href: _propTypes.default.string,
  /**
   * Optional function to be called on press e.g. for within-page navigation.
   */
  onPress: _propTypes.default.func,
  /**
   * Text stating the category of the content.
   */
  tag: _propTypes.default.string,
  /**
   * Section containing additional information, such as author and date.
   * Use `<PreviewCard.AuthorDate author={...} date={...} />` here for author and date.
   */
  footer: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.string]),
  /**
   * Text stating the title or headline of the story.
   */
  title: _propTypes.default.string.isRequired,
  /**
   * Full bleed image to be placed on the card, deprecated in favor of `fullBleedContent`.
   *
   * @deprecated
   */
  fullBleedImage: _propTypes.default.exact(_FullBleedContent.default.propTypes || {}),
  /**
   * Full bleed content to be placed on the card.
   */
  fullBleedContent: _propTypes.default.exact(_FullBleedContent.default.propTypes || {})
};
var _default = exports.default = (0, _componentsBase.withLinkRouter)(PreviewCard);